/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import apiFetch from "./apiFetch";

const SYNC_TYPE_OPTIONS = [
    { value: "Invoice", label: "Invoice" },
    { value: "CreditMemo", label: "Credit Memo" },
    { value: "SalesReceipt", label: "Sales Receipt" },
];

function DateRangePicker() {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [syncType, setSyncType] = useState(SYNC_TYPE_OPTIONS[0].value);
    const [error, setError] = useState("");
    const [syncId, setSyncId] = useState("");
    const [loading, setLoading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [summary, setSummary] = useState(null);
    const [status, setStatus] = useState("");

    const handleSyncTypeChange = (event) => {
        setSyncType(event.target.value);
        // Switching type mid-flow shouldn't show stale results from a previous sync.
        setSyncId("");
        setPolling(false);
        setSummary(null);
        setStatus("");
        if (error) setError("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!startDate || !endDate) {
            setError("Both start and end dates are required.");
            return;
        }
        if (startDate > endDate) {
            setError("Start date cannot be after end date.");
            return;
        }

        const realmId = sessionStorage.getItem("realmId");

        if (!realmId) {
            setError("Missing realm information. Please log in again.");
            return;
        }

        setError("");
        setLoading(true);

        try {
            const data = await apiFetch(
                `/api/sync-invoices?realmId=${realmId}&startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}&transactionType=${syncType}`
            );

            if (data === null) return; // Session expired, handled by apiFetch

            setSyncId(data.syncId);
            setPolling(true);
        } catch (err) {
            setError(err.message || "An error occurred while fetching Data.");
        } finally {
            setLoading(false);
        }
    };

    // Polling results at regular intervals
    useEffect(() => {
        let interval;

        if (polling && syncId) {
            interval = setInterval(async () => {
                try {
                    const data = await apiFetch(
                        `/api/sync-invoices/${syncId}/progress?realmId=${sessionStorage.getItem("realmId")}`
                    );

                    if (data === null) {
                        // Session expired, stop polling
                        setPolling(false);
                        clearInterval(interval);
                        return;
                    }

                    setStatus(data.status);
                    setSummary(data.summary);

                    if (data.status === "Completed" || data.status === "Failed") {
                        setPolling(false);
                        clearInterval(interval);
                    }
                } catch (err) {
                    setError("An error occurred while polling for sync status.");
                    setPolling(false);
                    clearInterval(interval);
                }
            }, 2000);
        }

        return () => clearInterval(interval);
    }, [polling, syncId]);

    const minDate = new Date(2022, 0, 1);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ alignItems: "center" }} gap={2} p={2}>
                <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
                >
                    Historical Data Sync
                </Typography>
            </Box>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "66%",
                    maxWidth: 800,
                    margin: "auto",
                    padding: 2,
                    boxShadow: 3,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h6" sx={{ textAlign: "center" }}>
                    Select Transaction Type and Date Range
                </Typography>
                <FormControl fullWidth>
                    <InputLabel id="sync-type-label">Transaction Type</InputLabel>
                    <Select
                        labelId="sync-type-label"
                        label="Transaction Type"
                        value={syncType}
                        onChange={handleSyncTypeChange}
                    >
                        {SYNC_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                    <DatePicker
                        label="Start Date"
                        value={startDate}
                        shouldDisableDate={(date) => date < minDate}
                        slotProps={{ textField: { fullWidth: true } }}
                        onChange={(newValue) => {
                            setStartDate(newValue);
                            if (error) setError("");
                        }}
                    />
                    <DatePicker
                        label="End Date"
                        value={endDate}
                        shouldDisableDate={(date) => date < minDate}
                        slotProps={{ textField: { fullWidth: true } }}
                        onChange={(newValue) => {
                            setEndDate(newValue);
                            if (error) setError("");
                        }}
                    />
                </Box>
                {error && (
                    <Typography color="error" sx={{ textAlign: "center" }}>
                        {error}
                    </Typography>
                )}
                {syncId && (
                    <Typography color="success" sx={{ textAlign: "center" }}>
                        Sync initiated successfully!
                    </Typography>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading || polling}
                    sx={{ alignSelf: "center" }}
                >
                    {loading || polling ? (
                        <>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            {polling ? "Polling..." : "Syncing..."}
                        </>
                    ) : (
                        "Sync Historical Transactions"
                    )}
                </Button>
            </Box>
            {polling && (
                <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
                    Current status: {status}
                </Typography>
            )}
            {summary && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: "66%",
                        margin: "auto",
                        maxWidth: 800,
                        padding: 2,
                        boxShadow: 3,
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                        Sync Summary
                    </Typography>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ fontWeight: "bold" }}>
                                        Status
                                    </TableCell>
                                    <TableCell style={{ fontWeight: "bold" }}>
                                        Count
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(summary).map(([key, value], index) => (
                                    <TableRow
                                        key={index}
                                        style={{
                                            backgroundColor:
                                                index % 2 === 0 ? "#f5f5f5" : "white",
                                        }}
                                    >
                                        <TableCell>{key.toUpperCase()}</TableCell>
                                        <TableCell>{value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </LocalizationProvider>
    );
}

export default DateRangePicker;
